import {FormGroup} from '@angular/forms';
import {getEditTypes} from '../../modules/flow/components/node-delegator/delegatorUtils';
import {newStepValue} from '../../modules/flow/services/flowBuilder/utils/constants';

export type StepType = 'action' | 'menu' | 'integration' | 'start';

export enum LoadingState {
    LOADING = 'LOADING',
    SUCCESS = 'SUCCESS',
    ERROR = 'ERROR'
}

export abstract class NodeBuilder {

    formGroup: FormGroup = undefined;

    readonly nodeType: string;
    canEdit: EditPermissions;
    nodeFlowDetails: NodeFlowDetails;
    uniqueID: string;

    /**
     * When the save button is clicked
     */
    abstract get getStepData(): {};

    // /**
    //  * Custom validation for the node
    //  */
    // abstract get isNodeValid(): boolean;

    /**
     * Constructs a FormGroup using the passed in stepData
     * @param stepData
     */
    abstract set buildForm(stepData: any);

    // /**
    //  * Custom validation for the node
    //  */
    get isNodeValid(): boolean {
        return this.isValidateForm();
    }

    isValidateForm(): boolean {
        this.formGroup.markAllAsTouched();
        return this.formGroup.valid;
    }

    getNextStep() {
        if (this.nodeFlowDetails) {
            return ++this.nodeFlowDetails.lastStepNumber;
        }
        console.error('Failed to get node details');
    }

    protected constructor(nodeType: string) {
        this.nodeType = nodeType;
    }

    getStepUniqueID(stepData: any) {
        this.uniqueID = stepData.metadata?.stepUniqueID ? stepData.metadata?.stepUniqueID : '';
    }

    setStepUniqueID(stepData: any): any {
        if (this.uniqueID !== '' && this.uniqueID) {
            stepData['metadata'].stepUniqueID = this.uniqueID;
        }
        return stepData;
    }

    set setNextStepValue(path: string) {
        const control = this.formGroup.get(path);
        if (control === null) { return; }
        if (control.value === newStepValue) {
            control.setValue('step' + this.getNextStep());
        }
    }

    set setAllNextStepValues(controlPaths: string[]) {
        for (const path of controlPaths) {
            this.setNextStepValue = path;
        }
    }
}

export interface NodeFlowDetails {
    currentFlowSteps: string[];
    flows: object;
    extraFields: object;
    lastStepNumber?: number;
    stepId: string;
}

export interface EditPermissions {
    canEditFlows: boolean;
    canEditMenuTextOnly: boolean;
    canEdit: boolean;
}

export abstract class NodeFormBuilder<T> {

    menu: {} = {};
    action: {} = {};
    integration: {} = {};
    private readonly nodeVersion: string;
    private readonly stepType: StepType;

    protected constructor(stepType: StepType, nodeType: string, nodeVersion: string = '1.0.0') {
        this.nodeVersion = nodeVersion;
        this.stepType = stepType;
        switch (stepType) {
            case 'action':
                this.action = {
                    'actionType': nodeType,
                };
                break;
            case 'menu':
                this.menu = {
                    'menuType': nodeType,
                };
                break;
            case 'integration': {
                this.integration = {};
                break;
            }
            default: {
                throw new Error('Unknown step type');
            }
        }
    }


    toForm(stepData: { [index: string]: any }, editPermissions: EditPermissions): FormGroup {
        const {editFlows} = getEditTypes(editPermissions);
        const formData: T = this.getFromData(stepData);

        return this.buildForm(formData, editFlows);
    }

    abstract getFromData(stepData: { [index: string]: any }): T;

    abstract buildForm(formData: T, editFlows: boolean): FormGroup;

    get nodeData(): {} {
        switch (this.stepType) {
            case 'action':
                return {
                    action: this.action,
                    'type': 'action',
                    metadata: {
                        nodeVersion: this.nodeVersion,
                    },
                };
            case 'menu':
                return {
                    menu: this.menu,
                    'type': 'menu',
                    metadata: {
                        nodeVersion: this.nodeVersion,
                    },
                };
            case 'integration': {
                return {
                    integration: this.integration,
                    'type': 'integration',
                    metadata: {
                        nodeVersion: this.nodeVersion,
                    },
                };
            }
        }
    }

    abstract setNodeData(nodeData: T);

    getValueOrDefault(value: string, defaultValue: any = ''): any {
        return value ? value : defaultValue;
    }

}
